import React from "react";
import { XAxis as ReChartsXAxis } from "recharts";
import type { TickProp } from "recharts/types/util/types";

import { getTime, secondsToMilliseconds } from "~/tools/date-time";
import { formatViewInterval } from "~/tools/date-time/format/format-view-interval";
import type { OpenInterval } from "~/tools/date-time/open-interval";
import type { Time } from "~/tools/date-time/time";
import { isNumber } from "~/tools/is-number";
import { theme } from "~/tools/twin-migration";
import { useNow } from "~/tools/use-now";

import type { SpecificRange } from "../../app/state/view/timeline/TimeRange";

const TICK = {
	fill: theme("colors.gray.500"),
} satisfies TickProp;

export interface XAxisProps {
	leftPadding: undefined | number | "no-gap";
	timeRange: SpecificRange;
}

export const XAxis: React.FC<XAxisProps> = React.memo(({ leftPadding, timeRange }) => {
	const interval = React.useMemo(
		() => [getTime(timeRange.start()), getTime(timeRange.endDate())] satisfies OpenInterval,
		[timeRange],
	);

	const intervalFormat = useNow({
		interval: secondsToMilliseconds(1),
		floor: "milliseconds",
		transform: (now) => formatViewInterval({ interval, viewTime: now, now }),
		compare: (prev, next) => prev.toString() === next.toString(),
	});

	const formatTick = React.useCallback((time: Time) => intervalFormat.format(time), [intervalFormat]);

	return (
		<ReChartsXAxis
			dataKey="time"
			domain={interval}
			fontSize={10}
			// the padding prevents overlaps of x-axis values to the y-axis values
			interval="preserveStartEnd"
			minTickGap={50}
			padding={isNumber(leftPadding) ? { left: leftPadding } : leftPadding}
			scale="time"
			stroke={theme("colors.gray.300")}
			tick={TICK}
			tickFormatter={formatTick}
			tickLine={false}
			type="number"
		/>
	);
});

if (import.meta.env.DEV) {
	XAxis.displayName = "TimeSeriesChart.XAxis";
}
